import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  addMembersToGroup,
  getAllGroupMembers,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { DirectoryGroup, DirectoryGroupMember } from '../../../../../types';
import GeneralDialog from '../../../../shared/general-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface CloneGroupMembersDialogProps {
  group: DirectoryGroup;
  open: boolean;
  handleClose: () => void;
  groupEmail: string;
  memberList: DirectoryGroupMember[];
  changeLogRoles: string[];
}

export default function CloneGroupMembersDialog({
  open,
  handleClose,
  groupEmail,
  changeLogRoles,
}: CloneGroupMembersDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [autoCompleteValue, setAutoCompleteValue] = useState('');
  const [gettingMembersLoading, setGettingMembersLoading] = useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: (data: DirectoryGroupMember[]) =>
      addMembersToGroup(groupEmail, data, changeLogMessage),
    onSuccess: (_returnData, data) => {
      setSnackbar({
        open: true,
        severity: data.length > 1 ? 'info' : 'success',
        message:
          data.length > 1
            ? 'The task to add the group members from the selected group has begun.'
            : 'Group member added successfully!',
      });

      if (data.length === 1) {
        queryClient.invalidateQueries({ queryKey: [UserKeys.GroupMembers] });
      }
      setAutoCompleteValue('');
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue adding the members from the selected group. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.group_details.clone_group_members, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groups,
      });
      setOpenChangeLogDialog(false);
    },
  });

  async function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.AddMemberToGroup.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      setGettingMembersLoading(true);
      const response = await getAllGroupMembers(autoCompleteValue);
      setGettingMembersLoading(false);
      mutate(response);
    }
  }

  return (
    <GeneralDialog
      open={open}
      handleClose={() => {
        setAutoCompleteValue('');
        handleClose();
      }}
      title="Add from Group"
      dialogContent={
        <Stack sx={{ px: 4 }} gap={3} alignItems={'center'}>
          <Typography
            sx={{
              mt: 2,
            }}
          >
            Select the group whose members you would like to add to this group:
          </Typography>
          <Box sx={{ width: 400 }}>
            <SharedAutocomplete
              sources={['groups']}
              autocompleteValue={autoCompleteValue}
              setAutoCompleteValue={setAutoCompleteValue}
            />
          </Box>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={async () => {
              setGettingMembersLoading(true);
              const response = await getAllGroupMembers(autoCompleteValue);
              setGettingMembersLoading(false);
              mutate(response);
            }}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Add',
        action: handleSubmit,
        loading: isPending || gettingMembersLoading,
        disabled:
          isPending || autoCompleteValue.length === 0 || gettingMembersLoading,
      }}
    />
  );
}
